import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, Icon, LoadingButton } from '@eventbrite/marmalade';
import { Modal } from '@/components/_shared/Modal/Modal';
import { redirectToSignIn } from '@/utils/utils';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { OrganizerInfo } from '@/components/EventDetails/OrganizerInfo';
import styles from './RemindMe.module.scss';
import { unremind, remind, getReminderStatus } from './remindMeClient';
import { RemindMeIcon } from './RemindMeIcon';
import { useUserData } from '@/hooks/useUserData';
import { ReminderSetIcon } from '@/components/_shared/Icons/ReminderSetIcon';

export const RemindMe: React.FC = () => {
  const [isReminded, setIsReminded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { formatMessage } = useIntl();
  const { organizer, id } = useBasicInfo();
  const { userId } = useUserData();
  const isAuthenticated = !!userId;

  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window?.location?.search);
    const remindMeActionRequested = urlParams.get('remindMe') === 'true';
    if (isAuthenticated && !isReminded && remindMeActionRequested) {
      setIsLoading(true);
      remind(id)
        .then(() => {
          if (!cancelled) {
            setIsReminded(true);
            setShowModal(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window?.location?.search);
    const remindMeActionRequested = urlParams.get('remindMe') === 'true';
    if (!isAuthenticated || !userId || !id || remindMeActionRequested) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getReminderStatus(userId, id)
      .then((hasReminder) => {
        if (!cancelled) {
          setIsReminded(hasReminder);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, id]);

  const toggleRemind = () => {
    setIsLoading(true);
    if (!isAuthenticated) {
      const url = new URL(window.location.href);
      url.searchParams.set('remindMe', 'true');
      url.searchParams.delete('overrideListingsOrigin');
      history.pushState(null, '', url.toString());
      redirectToSignIn();
      return;
    }

    if (isReminded) {
      unremind(id)
        .then(() => {
          setIsReminded(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }

    remind(id)
      .then(() => {
        setIsReminded(true);
        setShowModal(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <LoadingButton
        isLoading={isLoading}
        variant={isReminded ? 'secondary' : 'primary'}
        onClick={toggleRemind}
        aria-label={isReminded ? 'Remove reminder' : 'Set reminder'}
      >
        {isReminded ? (
          <div className={styles.reminderSetButtonVariant}>
            <ReminderSetIcon />
            <FormattedMessage id="Reminder set" defaultMessage="Reminder set" />
          </div>
        ) : (
          <FormattedMessage id="Remind me" defaultMessage="Remind me" />
        )}
      </LoadingButton>
      <Modal visible={showModal} onClose={() => setShowModal(false)} type="simple">
        <div className={styles.remindMeModalContent}>
          <Icon size="medium" className={styles.remindMeIcon}>
            <RemindMeIcon />
          </Icon>
          <Typography as="h2" variant="heading-md" className={styles.reminderSetTitle}>
            <FormattedMessage id="Reminder set!" defaultMessage="Reminder set!" />
          </Typography>
          <Typography as="p" variant="body-md" className={styles.reminderSetDescription}>
            <FormattedMessage
              id="We'll email you an hour before tickets go on sale. Manage your reminder on the event page."
              defaultMessage="We'll email you an hour before tickets go on sale. Manage your reminder on the event page."
            />
          </Typography>
          <section
            className={styles.organizerInfoSection}
            aria-label={formatMessage({ id: 'Organizer profile', defaultMessage: 'Organizer profile' })}
          >
            <OrganizerInfo organizer={organizer} />
          </section>
        </div>
      </Modal>
    </div>
  );
};
